import {type CachedDataWithSize, type LongTermCache} from '../types';
import {logPerfAndErrors} from '../util';
import {clearOPFS, getOPFSFileTextContent, storeTextFileInOPFS} from './util';

export * from './errors';
export * from './util';

/**
 * OPFSCache is a legacy implementation of the LongTermCache interface using the
 * Origin Private File System (OPFS) available in modern browsers. This class
 * provides methods to interact with the browser's file storage, allowing for
 * storing, retrieving, checking, and deleting cached data. It is designed to
 * handle JSON-serializable data and includes performance logging for cache
 * operations.
 *
 * While OPFS is a general-purpose file storage system that allows web applications
 * to store and manage files in a secure and private manner, the Cache API should
 * be preferred for caching purposes. The Cache API is specifically designed for
 * managing network request/response caching and is integrated with the browser's
 * fetch API, making it more suitable for caching HTTP responses. More information
 * can be found on the MDN Web Docs:
 * https://developer.mozilla.org/en-US/docs/Web/API/File_System_API/Origin_private_file_system
 */
export class OPFSCache implements LongTermCache {
  async get<T>(fileName: string): Promise<CachedDataWithSize<T> | null> {
    return (
      (await logPerfAndErrors(`fetchFromCache`, async () => {
        const jsonStr = await getOPFSFileTextContent(fileName);
        const data = JSON.parse(jsonStr);
        const size = jsonStr.length;
        return {data, bytesReadFromCache: size};
      })) || null
    );
  }

  async hasKey(fileName: string): Promise<boolean> {
    try {
      const root = await navigator.storage.getDirectory();
      await root.getFileHandle(fileName);
      return true;
    } catch (err) {
      return false;
    }
  }

  async set<T>(fileName: string, data: T): Promise<void> {
    return logPerfAndErrors(`storeInCache`, async () => {
      if (data === undefined) {
        throw new Error(`data cannot be undefined (must be JSON-serializable)`);
      }
      const jsonStr = JSON.stringify(data);
      await storeTextFileInOPFS(fileName, jsonStr);
    });
  }

  async remove(fileName: string): Promise<void> {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry(fileName);
  }

  async clear(): Promise<void> {
    await clearOPFS();
  }
}
